import isEqual from 'lodash/isEqual';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { MapRef } from 'react-map-gl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { AppHeader } from '@components/AppHeader/AppHeader';
import DateAndTime from '@components/DateAndTime/DateAndTime';
import Loader from '@components/Loader/Loader';
import MapMarkers from '@components/Map/MapMarkers/MapMarkers';
import MapRoute from '@components/Map/MapRoute/MapRoute';
import MapView, { type Region } from '@components/Map/MapView/MapView';
import { TripDetailInfo } from '@components/TripDetailInfo/TripDetailInfo';
import TripStatus from '@components/TripStatus/TripStatus';
import { ERoutes } from '@enums/routes.enum';
import useApi from '@hooks/useApi';
import useTripSocket from '@hooks/useTripSocket';
import type { RootState } from '@store/store';
import { resetCurrentTrip, type Trip } from '@store/trips/tripsSlice';

import classes from './TripInfo.module.scss';
import { handleMakeRegion } from './TripInfo.utils';

const TripInfo = () => {
	const [isCollapse, setIsCollapse] = useState<boolean>(false);

	const { id } = useParams<{ id: string }>();
	const isRefreshing = useSelector((state: RootState) => state.auth.isRefreshing);
	const trips = useSelector((state: RootState) => state.trips.data);
	const currentTrip = useSelector((state: RootState) => state.trips.currentTrip?.id);
	const status = useSelector((state: RootState) => state.trips.currentTrip?.status);
	const date = useSelector((state: RootState) => state.trips.currentTrip?.date);
	const route = useSelector((state: RootState) => state.trips.currentTrip?.route);
	const stops = useSelector((state: RootState) => state.trips.currentTrip?.stops || [], isEqual);

	const showGoBack = trips?.length > 1;
	const navigate = useNavigate();
	const socketTripsIds = useMemo(() => trips?.map((trip: Trip) => trip.id) || [], [trips]);
	useTripSocket(socketTripsIds);
	const { getSingleTrip } = useApi();
	const dispatch = useDispatch();
	const mapRef = useRef<MapRef>(null);

	const region = useMemo<Region | undefined>(() => {
		if (route) {
			return handleMakeRegion(route.coordinates, isCollapse);
		}
		return undefined;
	}, [route, isCollapse]);

	const handleGoBack = () => {
		dispatch(resetCurrentTrip());
		navigate(ERoutes.trips);
	};

	const handleCollapse = () => {
		setIsCollapse((prev) => !prev);
	};

	const handleOnRefresh = () => {
		getSingleTrip(Number(id));
	};

	useEffect(() => {
		if (!isRefreshing) {
			handleOnRefresh();
		}
	}, [isRefreshing]);

	return currentTrip ? (
		<div className={classes['container']}>
			<AppHeader goBack={showGoBack ? handleGoBack : undefined} />
			{date && status && (
				<div className={classes['headerContainer']}>
					<TripStatus status={status} />
					<DateAndTime date={date} size={16} />
				</div>
			)}
			{region && (
				<MapView fullScreen={isCollapse} mapRef={mapRef} region={region}>
					{route && <MapRoute route={route} />}
					{route && stops.length > 0 && <MapMarkers markers={stops} />}
				</MapView>
			)}
			<TripDetailInfo isCollapse={isCollapse} onCollapse={handleCollapse} />
		</div>
	) : (
		<Loader />
	);
};

export default TripInfo;
